import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { clearTokens, getTokens, setTokens, type AuthTokens } from './token-store';

// Shape every API response shares: { message, data, meta }. data is null on error.
export interface Envelope<T> {
  message: string;
  data: T;
  meta: Record<string, unknown> | null;
}

// Marks a request we've already retried after a refresh, so a second 401 can't loop forever.
interface RetryConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const api = axios.create({ baseURL });

// Attach the current access token to every request.
api.interceptors.request.use((config) => {
  const tokens = getTokens();
  if (tokens?.access_token) {
    config.headers.set('Authorization', `Bearer ${tokens.access_token}`);
  }
  return config;
});

// Single-flight refresh: many requests can 401 at once, but we only call /auth/refresh once
// and everyone awaits the same promise. Uses a bare axios call so it skips these interceptors.
let refreshing: Promise<string | null> | null = null;

async function runRefresh(): Promise<string | null> {
  const tokens = getTokens();
  if (!tokens?.refresh_token) return null;
  try {
    const res = await axios.post<Envelope<AuthTokens>>(`${baseURL}/auth/refresh`, {
      refresh_token: tokens.refresh_token,
    });
    setTokens(res.data.data);
    return res.data.data.access_token;
  } catch {
    // Refresh token dead → the session is over. Clear it and let the app react (redirect to /login).
    clearTokens();
    window.dispatchEvent(new Event('auth:logout'));
    return null;
  }
}

// On a 401, transparently refresh the access token once and replay the original request.
// Auth endpoints are excluded so a bad-password 401 surfaces as-is instead of triggering refresh.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const isAuthCall = original?.url?.includes('/auth/');
    if (error.response?.status === 401 && original && !original._retry && !isAuthCall && getTokens()?.refresh_token) {
      original._retry = true;
      refreshing ??= runRefresh().finally(() => {
        refreshing = null;
      });
      const token = await refreshing;
      if (token) {
        original.headers.set('Authorization', `Bearer ${token}`);
        return api(original);
      }
    }
    return Promise.reject(error);
  },
);

// One field-level complaint from the API's Zod handler (`meta.issues`).
interface ValidationIssue {
  path: string;
  message: string;
}

// A validation failure answers with the same flat line for every field — "Invalid request body" —
// while `meta.issues` carries the field that actually failed. Dropping that made a 400 read as an
// unexplained refusal, so append the issues to the message: "Invalid request body: sport_id —
// Invalid uuid". Capped at three so a whole-form rejection stays a toast, not a wall of text.
function issueDetail(meta: Record<string, unknown> | null | undefined): string {
  const raw = meta?.['issues'];
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const parts = (raw as ValidationIssue[])
    .slice(0, 3)
    .map((i) => (i.path ? `${i.path} — ${i.message}` : i.message));
  const more = raw.length > 3 ? ` (+${raw.length - 3} more)` : '';
  return `: ${parts.join('; ')}${more}`;
}

// Pull the human-readable message out of our error envelope for surfacing in the UI.
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const axiosErr = err as AxiosError<Envelope<null>>;
  const envelope = axiosErr?.response?.data;
  if (envelope?.message) return envelope.message + issueDetail(envelope.meta);
  return axiosErr?.message ?? fallback;
}

// Unwrap a successful envelope to its data payload.
export function unwrap<T>(envelope: Envelope<T>): T {
  return envelope.data;
}
